import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useLayoutEffect,
} from 'react'
import PropTypes from 'prop-types'
import { DateTime } from 'luxon'
import Box from '@material-ui/core/Box'
import { makeStyles } from '@material-ui/core/styles'
import Error from 'components/error'
import AcademyResourceCalendar from './academy-resource-calendar'
import { useAcademyCoachCalendarLayers } from './use-academy-coach-schedules'
import { buildScheduleRange } from './academy-schedule-range-utils'
import { SCHEDULE_BLOCK_TIMES_REFETCH_EVENT } from '../schedule-block-times-refetch'
import { SCHEDULE_BOOKINGS_REFETCH_EVENT } from '../schedule-bookings-refetch'
import { VIEW_DAY, VIEW_WEEK } from '../schedule-date-ranges'

const useStyles = makeStyles({
  layer: {
    position: 'absolute',
    inset: 0,
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    minWidth: 0,
  },
})

const AcademyScheduleDataCalendar = React.memo(
  function AcademyScheduleDataCalendar({
    academyId,
    coaches,
    coachColorMap,
    onEventClick,
    onRegisterRangeHandler,
    onLoadingChange,
    onCoachStatesChange,
  }) {
    const classes = useStyles()
    const [range, setRange] = useState(() =>
      buildScheduleRange(VIEW_WEEK, DateTime.local()),
    )
    const coachIds = useMemo(() => coaches.map(c => c.id), [coaches])

    useEffect(() => {
      onRegisterRangeHandler?.(setRange)
      return () => onRegisterRangeHandler?.(null)
    }, [onRegisterRangeHandler])

    const from = range?.from
    const to = range?.to
    const anchorDate = range?.anchorDate
    const viewMode = range?.viewMode

    const {
      calendarLoading,
      error,
      coachOccurrencesMap,
      coachStates,
      refetch,
    } = useAcademyCoachCalendarLayers({
      from,
      to,
      coachIds,
    })

    const awaitingCoaches = coachIds.length === 0
    const effectiveLoading = calendarLoading || awaitingCoaches

    useLayoutEffect(() => {
      onLoadingChange?.(effectiveLoading)
    }, [effectiveLoading, onLoadingChange])

    useEffect(() => {
      onCoachStatesChange?.(coachStates)
    }, [coachStates, onCoachStatesChange])

    const refetchRef = useRef(refetch)
    refetchRef.current = refetch

    useEffect(() => {
      const onRefetch = () => {
        if (academyId) refetchRef.current()
      }
      window.addEventListener(SCHEDULE_BOOKINGS_REFETCH_EVENT, onRefetch)
      window.addEventListener(SCHEDULE_BLOCK_TIMES_REFETCH_EVENT, onRefetch)
      return () => {
        window.removeEventListener(SCHEDULE_BOOKINGS_REFETCH_EVENT, onRefetch)
        window.removeEventListener(
          SCHEDULE_BLOCK_TIMES_REFETCH_EVENT,
          onRefetch,
        )
      }
    }, [academyId])

    const selectedOccurrencesMap = useMemo(() => {
      const map = {}
      for (const coach of coaches) {
        map[coach.id] = coachOccurrencesMap[coach.id] || []
      }
      return map
    }, [coaches, coachOccurrencesMap])

    if (!range || !anchorDate || !coachIds.length) {
      return null
    }

    if (error) {
      return (
        <Box className={classes.layer}>
          <Error error={error} />
        </Box>
      )
    }

    if (calendarLoading) {
      return null
    }

    return (
      <Box className={classes.layer}>
        <AcademyResourceCalendar
          coaches={coaches}
          coachColorMap={coachColorMap}
          coachOccurrencesMap={selectedOccurrencesMap}
          anchorDate={anchorDate}
          isSingleDay={viewMode === VIEW_DAY}
          onEventClick={onEventClick}
        />
      </Box>
    )
  },
)

AcademyScheduleDataCalendar.propTypes = {
  academyId: PropTypes.string,
  coaches: PropTypes.array.isRequired,
  coachColorMap: PropTypes.object.isRequired,
  onEventClick: PropTypes.func,
  onRegisterRangeHandler: PropTypes.func,
  onLoadingChange: PropTypes.func,
  onCoachStatesChange: PropTypes.func,
}

export default AcademyScheduleDataCalendar
